import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, object=logged in

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false)); // the api interceptor already attempts /auth/refresh on 401
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  }, []);

  // Keep staff sessions alive during real activity (typing/clicking make no API
  // calls, so without this a long editing session hits the 60-min inactivity logout).
  useEffect(() => {
    if (!user || !["admin", "agent"].includes(user.role)) return;
    let lastInput = Date.now();
    let lastFired = 0;
    const onInput = () => {
      const n = Date.now();
      if (n - lastFired > 30000) { lastFired = n; lastInput = n; }
    };
    window.addEventListener("pointerdown", onInput);
    window.addEventListener("keydown", onInput);
    const t = setInterval(() => {
      if (document.visibilityState === "visible" && Date.now() - lastInput < 55 * 60 * 1000) {
        api.post("/auth/refresh").catch(() => {});
      }
    }, 4 * 60 * 1000);
    return () => {
      window.removeEventListener("pointerdown", onInput);
      window.removeEventListener("keydown", onInput);
      clearInterval(t);
    };
  }, [user]);

  return <AuthContext.Provider value={{ user, setUser, login, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
